import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tag, Search, Calendar, RefreshCw, CheckCircle2, Clock3, Hourglass } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT"); }
  catch { return iso; }
};

const fmtDiscount = (p) => {
  const parts = [];
  for (const k of ["sconto_1", "sconto_2", "sconto_3", "sconto_4"]) {
    const v = p[k];
    if (v !== null && v !== undefined && v !== 0) parts.push(`${v}%`);
  }
  return parts.length > 0 ? parts.join(" + ") : "-";
};

const promoStatus = (p) => {
  const today = new Date().toISOString().slice(0, 10);
  if (p.data_termine < today) return "expired";
  if (p.data_inizio > today) return "future";
  return "active";
};

const STATUS_BADGE = {
  active:  { label: "Attiva",   icon: CheckCircle2, className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  future:  { label: "Futura",   icon: Hourglass,   className: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  expired: { label: "Scaduta",  icon: Clock3,      className: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};

export default function AdminPromotions() {
  const { authAxios } = useAuth();
  const [promotions, setPromotions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        authAxios.get("/promotions"),
        authAxios.get("/promotions/stats"),
      ]);
      setPromotions(pRes.data);
      setStats(sRes.data);
    } catch { toast.error("Errore caricamento promozioni"); }
    finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return promotions.filter((p) => {
      if (filterStatus !== "all" && promoStatus(p) !== filterStatus) return false;
      if (!q) return true;
      return [
        p.cod_cliente, p.cod_articolo, p.categ_merc, p.descr_promo,
        String(p.brand_code || ""), String(p.cod_int_promo || ""),
      ].filter(Boolean).some((f) => String(f).toLowerCase().includes(q));
    });
  }, [promotions, filterStatus, search]);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Promozioni" />

        <div className="mb-6 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Promozioni Clienti</h1>
            <p className="text-zinc-400 text-sm">Sconti e prezzi netti a tempo per singolo cliente</p>
          </div>
          <Button variant="outline" onClick={fetchAll} className="border-white/10 text-white" data-testid="refresh-promos">
            <RefreshCw className="w-4 h-4 mr-2" />Aggiorna
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatBox label="Totale" value={stats.total} color="zinc" />
            <StatBox label="Attive" value={stats.active} color="emerald" />
            <StatBox label="Scadute" value={stats.expired} color="zinc" />
            <StatBox label="Future" value={stats.future} color="blue" />
          </div>
        )}

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Cerca cliente, articolo, categoria..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10"
              data-testid="promo-search"
            />
          </div>
          <Tabs value={filterStatus} onValueChange={setFilterStatus} className="shrink-0">
            <TabsList className="bg-[#18181b] border border-white/5">
              <TabsTrigger value="active" data-testid="tab-active">Attive</TabsTrigger>
              <TabsTrigger value="future" data-testid="tab-future">Future</TabsTrigger>
              <TabsTrigger value="expired" data-testid="tab-expired">Scadute</TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all">Tutte</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Card className="bg-[#18181b] border-white/5 overflow-x-auto">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-500" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Tag className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>Nessuna promozione</p>
                <p className="text-xs mt-1">Carica il file da <span className="text-amber-400">/admin/imports → Promozioni</span></p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-white/5">
                  <tr className="text-left text-zinc-400 text-xs uppercase">
                    <th className="px-3 py-2">Stato</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2">Target</th>
                    <th className="px-3 py-2">Periodo</th>
                    <th className="px-3 py-2 text-right">Sconto</th>
                    <th className="px-3 py-2 text-right">Prezzo netto</th>
                    <th className="px-3 py-2 text-right">Omaggio</th>
                  </tr>
                </thead>
                <tbody data-testid="promotions-tbody">
                  {filtered.slice(0, 500).map((p) => {
                    const st = promoStatus(p);
                    const cfg = STATUS_BADGE[st];
                    const Icon = cfg.icon;
                    return (
                      <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={`${cfg.className} text-[10px]`}>
                            <Icon className="w-3 h-3 mr-1" />{cfg.label}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 font-mono text-white text-xs">{p.cod_cliente}</td>
                        <td className="px-3 py-2 text-zinc-300 text-xs">
                          {p.cod_articolo && <Badge variant="outline" className="border-blue-500/30 text-blue-300 mr-1">SKU {p.cod_articolo}</Badge>}
                          {p.categ_merc && <Badge variant="outline" className="border-amber-500/30 text-amber-300 mr-1">Cat. {p.categ_merc}</Badge>}
                          {p.brand_code && <Badge variant="outline" className="border-purple-500/30 text-purple-300 mr-1">Marca {p.brand_code}</Badge>}
                          {p.cod_categ && <Badge variant="outline" className="border-cyan-500/30 text-cyan-300 mr-1">Cat.pag. {p.cod_categ}</Badge>}
                        </td>
                        <td className="px-3 py-2 text-zinc-400 text-xs">
                          <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(p.data_inizio)}</div>
                          <div className="flex items-center gap-1 text-zinc-500"><Calendar className="w-3 h-3" />{fmtDate(p.data_termine)}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-zinc-200">{fmtDiscount(p)}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-300">
                          {p.prezzo_netto ? p.prezzo_netto.toLocaleString("it-IT", { style: "currency", currency: "EUR" }) : "-"}
                        </td>
                        <td className="px-3 py-2 text-right text-zinc-400 text-xs">
                          {p.qta_omaggio ? `${p.qta_omaggio} pz${p.multiplo_di ? ` ogni ${p.multiplo_di}` : ""}` : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {filtered.length > 500 && (
              <div className="text-center py-3 text-xs text-zinc-500">
                Mostrate prime 500 di {filtered.length}. Affina la ricerca.
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function StatBox({ label, value, color }) {
  const colors = {
    zinc: "border-white/10 text-white",
    emerald: "border-emerald-500/30 text-emerald-300",
    blue: "border-blue-500/30 text-blue-300",
  };
  return (
    <div className={`p-4 rounded-xl border bg-[#18181b] ${colors[color]}`}>
      <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium mb-1">{label}</p>
      <p className="font-heading text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}
